import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { documentsApi } from '../services/backendApi';
import type { Document, DocumentType } from '../types';

interface UploadOptions {
  qualificationId?: string;
  referenceId?: string;
  verificationId?: string;
}

interface UseDocumentsReturn {
  documents: Document[];
  loading: boolean;
  error: string | null;
  fetchDocuments: () => Promise<void>;
  uploadDocument: (file: File, documentType: DocumentType, options?: UploadOptions) => Promise<{ success: boolean; error?: string }>;
  deleteDocument: (id: string) => Promise<{ success: boolean; error?: string }>;
  clearError: () => void;
}

export const useDocuments = (): UseDocumentsReturn => {
  const { isAuthenticated } = useAuth();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDocuments = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    setError(null);

    try {
      const data = await documentsApi.getAll();
      setDocuments(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const uploadDocument = useCallback(async (
    file: File,
    documentType: DocumentType,
    options?: UploadOptions,
  ) => {
    setLoading(true);
    setError(null);

    try {
      const created = await documentsApi.upload(file, {
        document_type: documentType,
        qualification_id: options?.qualificationId,
        reference_id: options?.referenceId,
        verification_id: options?.verificationId,
      });
      setDocuments(prev => [created, ...prev]);
      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to upload document';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteDocument = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      await documentsApi.remove(id);
      setDocuments(prev => prev.filter(d => d.id !== id));
      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete document';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    documents,
    loading,
    error,
    fetchDocuments,
    uploadDocument,
    deleteDocument,
    clearError,
  };
};
